
import { useState } from "react";
import { ArticleTag } from "@/types/article-types";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useTagOperations = () => {
  const [tags, setTags] = useState<ArticleTag[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadTags = async (retryCount = 0, maxRetries = 3) => {
    if (loading) return; // Prevent concurrent requests
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('article_tags')
        .select('*')
        .order('name');

      if (error) throw error;
      setTags(data || []);
    } catch (error: any) {
      console.error('Error loading tags:', error);
      
      // Retry logic for network errors
      if (retryCount < maxRetries && 
          (error.message.includes('Failed to fetch') || 
           error.code === 'ERR_INSUFFICIENT_RESOURCES')) {
        console.log(`Retrying loadTags (${retryCount + 1}/${maxRetries})...`);
        setTimeout(() => {
          loadTags(retryCount + 1, maxRetries);
        }, 1000 * Math.pow(2, retryCount)); // Exponential backoff
        return;
      }
      
      toast({
        variant: "destructive",
        title: "Error loading tags",
        description: error.message || "Failed to load tags"
      });
    } finally {
      setLoading(false);
    }
  };

  const createTag = async (name: string) => {
    try {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      
      const { data, error } = await supabase
        .from('article_tags')
        .insert({ name, slug })
        .select()
        .single();

      if (error) throw error;
      
      setTags(prevTags => [...prevTags, data]);
      
      toast({
        title: "Tag created",
        description: `The tag "${name}" has been successfully created.`
      });
      
      return data;
    } catch (error: any) {
      console.error('Error creating tag:', error);
      toast({
        variant: "destructive",
        title: "Error creating tag",
        description: error.message || "Failed to create tag"
      });
      return null;
    }
  };

  return {
    tags,
    loading,
    loadTags,
    createTag
  };
};
